import React from "react";
import { Card, Box, Stack, IconButton } from "@mui/material";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import EmailIcon from "@mui/icons-material/Email";
import CervinoText from "../typography/CervinoText";

export default function HorizontalLogoContactCard({
                                                      backgroundColor = "#7A3E23",

                                                      // Only pass these two (as you want)
                                                      patternSrc1,
                                                      patternSrc2,

                                                      logoSrc,
                                                      logoAlt = "Logo",

                                                      whatsappNumber,
                                                      whatsappMessage = "",
                                                      email,
                                                      emailSubject = "",
                                                      emailBody = "",
                                                      radius = 4,
                                                      contactLabel = "Contact Us",
                                                      onWhatsappClick,
                                                      onEmailClick,
                                                      sx,
                                                  }) {
    const hasWhatsapp = Boolean(whatsappNumber);
    const hasEmail = Boolean(email);

    const waHref = hasWhatsapp
        ? buildWhatsAppLink(whatsappNumber, whatsappMessage)
        : "#";
    const mailHref = hasEmail ? buildMailtoLink(email, emailSubject, emailBody) : "#";

    /**
     * ✅ EASY TUNING ZONE (no prop passing needed)
     * Move/scale/rotate the background decorations here.
     *
     * Tips:
     * - Change left/top/right/bottom to move around
     * - Change width to scale
     * - Change opacity for strength
     * - Change transform translate/rotate to fine-tune positioning
     */
    const pattern1Sx = {
        position: "absolute",
        zIndex: 0,
        pointerEvents: "none",
        userSelect: "none",
        WebkitUserDrag: "none",
        opacity: 0.16,
        mixBlendMode: "soft-light",
        left: { xs: -80, sm: -100, md: -90, lg: -100, xl: -110 },
        top: { xs: -40, sm: -80, md: -60, lg: -65, xl: -80 },
        width: { xs: 200, sm: 340, md: 400, lg: 480, xl: 580 },
        height: "auto",
        transform: "rotate(90deg)",
    };

    const pattern2Sx = {
        position: "absolute",
        zIndex: 0,
        pointerEvents: "none",
        userSelect: "none",
        WebkitUserDrag: "none",
        opacity: 0.16,
        mixBlendMode: "soft-light",
        left: { xs: 240, sm: 560, md: 750, lg: 1100, xl: 1550 },
        top: { xs: -330, sm: -350, md: -500, lg: -610, xl: -720 },
        width: { xs: 400, sm: 450, md: 600, lg: 800, xl: 900 },
        height: "auto",
        transform: "rotate(-145deg)",
    };

    return (
        <Card
            elevation={0}
            sx={{
                position: "relative",
                width: "100%",
                height: { xs: 180, sm: 280, md: 380, lg: 480, xl: 580 },
                borderRadius: radius,
                overflow: "hidden",
                backgroundColor,
                ...sx,
            }}
        >
            {/* ✅ Decorative background images (two layers) */}
            {patternSrc1 ? (
                <Box component="img" src={patternSrc1} alt="" sx={pattern1Sx} />
            ) : null}

            {patternSrc2 ? (
                <Box component="img" src={patternSrc2} alt="" sx={pattern2Sx} />
            ) : null}

            {/* Center Logo */}
            <Box
                sx={{
                    position: "relative",
                    zIndex: 1,
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    px: 2,
                }}
            >
                <Box
                    component="img"
                    src={logoSrc}
                    alt={logoAlt}
                    onError={(e) => console.error("Logo failed to load:", logoSrc, e)}
                    sx={{
                        display: "block",
                        width: "auto",
                        height: "auto",
                        maxHeight: "100%",
                        maxWidth: "95%",
                        objectFit: "contain",
                        userSelect: "none",
                        pointerEvents: "none",
                    }}
                />
            </Box>

            {/* Bottom-right Contact Stack */}
            <Stack
                spacing={0.6}
                alignItems="flex-end"
                sx={{
                    position: "absolute",
                    right: {xs: 18, sm: 22, md: 30},
                    bottom: 18,
                    zIndex: 2,
                }}
            >
                {/* Hide on xs */}
                <CervinoText
                    variant="caption"
                    sx={{
                        display: { xs: "none", sm: "block" },
                        fontWeight: 900,
                        color: "#fff",
                        fontSize: { sm: "1.2rem", md: "1.5rem" },
                        lineHeight: 1,
                    }}
                >
                    {contactLabel}
                </CervinoText>

                {/* Icons row with animation + responsive sizes */}
                <Stack direction="row" spacing={0.75} alignItems="center" sx={bounceKeyframes}>
                    <IconButton
                        onClick={() => {
                            if (!hasWhatsapp) return;
                            onWhatsappClick?.();
                            window.open(waHref, "_blank", "noopener,noreferrer");
                        }}
                        disabled={!hasWhatsapp}
                        sx={{ ...animatedIconSx, animationDelay: "0s" }}
                        aria-label="WhatsApp"
                    >
                        <WhatsAppIcon
                            sx={{
                                fontSize: { xs: 20, sm: 24, md: 28, lg: 32 },
                            }}
                        />
                    </IconButton>

                    <IconButton
                        onClick={() => {
                            if (!hasEmail) return;
                            onEmailClick?.();
                            window.location.href = mailHref;
                        }}
                        disabled={!hasEmail}
                        sx={{ ...animatedIconSx, animationDelay: "0.25s" }}
                        aria-label="Email"
                    >
                        <EmailIcon
                            sx={{
                                fontSize: { xs: 20, sm: 24, md: 28, lg: 32 },
                            }}
                        />
                    </IconButton>
                </Stack>
            </Stack>
        </Card>
    );
}

/** Animation keyframes */
const bounceKeyframes = {
    "@keyframes bounce": {
        "0%, 100%": {
            transform: "translateY(0)",
            boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
        },
        "50%": {
            transform: "translateY(-6px)",
            boxShadow: "0 10px 16px rgba(0,0,0,0.35)",
        },
    },
};

const animatedIconSx = {
    color: "#fff",
    borderRadius: "50%",
    animation: "bounce 1.8s ease-in-out infinite",
    boxShadow: "0 4px 8px rgba(0,0,0,0.25)",
    p: { xs: 0.5, sm: 0.75, md: 1 },
    "&:hover": {
        animationPlayState: "paused",
        backgroundColor: "rgba(255,255,255,0.08)",
    },
};

/** Helpers */
function buildWhatsAppLink(numberRaw, message) {
    const number = String(numberRaw).replace(/[^\d]/g, "");
    const text = message ? `?text=${encodeURIComponent(message)}` : "";
    return `https://wa.me/${number}${text}`;
}

function buildMailtoLink(email, subject, body) {
    const params = new URLSearchParams();
    if (subject) params.set("subject", subject);
    if (body) params.set("body", body);
    const query = params.toString();
    return `mailto:${email}${query ? `?${query}` : ""}`;
}
